// src/components/landing/NavigationBar.tsx
"use client";

import Link from 'next/link';
import { ParkingSquare, Menu as MenuIcon, X as XIcon, ChevronDown, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useTheme } from "next-themes";
import type { Dictionary, Locale } from '@/lib/i18n';
import { supportedLocales, defaultLocale } from '@/lib/i18n';
import { LanguageFlag } from "@/components/ui/flags";

interface NavigationBarProps {
  dictionary: Dictionary['navigation'];
  currentLocale: Locale;
}

export function NavigationBar({ dictionary, currentLocale }: NavigationBarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();

  const navLinks = [
    { href: "#features", labelKey: "features" },
    { href: "#benefits", labelKey: "benefits" },
    { href: "#testimonials", labelKey: "testimonials" },
    { href: "#contact", labelKey: "contact" },
  ];

  const languageNames: Record<Locale, string> = {
    'en-US': 'English',
    'cs-CZ': 'Čeština',
    'de-DE': 'Deutsch',
    'es-ES': 'Español',
    'sk-SK': 'Slovenčina',
    'pl-PL': 'Polski',
    'hu-HU': 'Magyar',
    'uk-UA': 'Українська',
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  const getLocalizedPath = (locale: Locale) => {
    if (!pathname) return `/${locale}`;
    const segments = pathname.split('/').filter(Boolean); // Filter out empty strings from split
  
    // Check if the first segment is a locale
    const currentPathLocale = supportedLocales.find(loc => loc === segments[0]);
  
    if (currentPathLocale) {
      // If a locale is found, replace it with the new one
      segments[0] = locale;
    } else {
      // If no locale is found, insert the new one at the beginning
      segments.unshift(locale);
    }
  
    return `/${segments.join('/')}`;
  };
  
  const getHomePath = () => `/${currentLocale}`;
  const getNavLinkHref = (hash: string) => `/${currentLocale}${hash}`;


  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b">
      <div className="container mx-auto flex items-center justify-between py-2 px-4 h-16">
        <Link href={getHomePath()} className="flex items-center gap-2">
          <ParkingSquare className="h-8 w-8 text-primary" />
          <span className="text-2xl font-bold">ParkWise</span>
        </Link>

        {/* --- Desktop Navigation --- */}
        <div className="hidden md:flex items-center space-x-4">
          {navLinks.map(link => (
            <Link key={link.labelKey}
                  href={getNavLinkHref(link.href)}
                  className="text-sm font-medium hover:text-primary">
              {dictionary[link.labelKey as keyof typeof dictionary]}
            </Link>
          ))}
          
           <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle theme">
            <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <LanguageFlag locale={currentLocale} className="h-5 w-5 mr-1 rounded-sm" />
                {languageNames[currentLocale]}
                <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {supportedLocales.map(loc => (
                <DropdownMenuItem key={loc} asChild>
                  <Link href={getLocalizedPath(loc)} className="flex items-center gap-2">
                    <LanguageFlag locale={loc} className="h-5 w-5 rounded-sm" />
                    <span>{languageNames[loc]}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link href={`/${currentLocale}/signin`} className="text-sm">
            <Button variant="ghost" size="sm">{dictionary.signIn}</Button>
          </Link>
          <Link href={`/${currentLocale}/signup`} className="text-sm">
            <Button size="sm">{dictionary.signUp}</Button>
          </Link>
        </div>

        {/* --- Mobile Menu Toggle --- */}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* --- Mobile Dropdown Menu --- */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background border-t pb-4">
          <nav className="flex flex-col items-center space-y-2 pt-2">
            {navLinks.map(link => (
              <Link key={link.labelKey}
                    href={getNavLinkHref(link.href)}
                    className="w-full text-center py-2 font-medium"
                    onClick={toggleMobileMenu}>
                {dictionary[link.labelKey as keyof typeof dictionary]}
              </Link>
            ))}

            <div className="mt-4 w-full px-4 space-y-2">
                <Button variant="outline" className="w-full justify-between" onClick={toggleTheme}>
                   <span>Toggle Theme</span>
                   {theme === 'light' ? <Sun className="h-[1.2rem] w-[1.2rem]" /> : <Moon className="h-[1.2rem] w-[1.2rem]" />}
                </Button>
               <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <LanguageFlag locale={currentLocale} className="h-5 w-5" />
                      {languageNames[currentLocale]}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-[calc(100vw-2rem)]">
                  {supportedLocales.map(loc => (
                    <DropdownMenuItem key={loc} asChild>
                      <Link href={getLocalizedPath(loc)} onClick={toggleMobileMenu} className="flex items-center gap-2">
                        <LanguageFlag locale={loc} className="h-5 w-5 rounded-sm" />
                        <span>{languageNames[loc]}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link href={`/${currentLocale}/signin`} onClick={toggleMobileMenu} className="block">
                <Button variant="outline" className="w-full">{dictionary.signIn}</Button>
              </Link>
              <Link href={`/${currentLocale}/signup`} onClick={toggleMobileMenu} className="block">
                <Button className="w-full">{dictionary.signUp}</Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
);
}
